'use client';

import { useQueryClient } from '@tanstack/react-query';
import Decimal from 'decimal.js';
import { useTranslations } from 'next-intl';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { capture, type OrderEventProps } from '@/lib/analytics/posthog';
import { fetchActiveOrders } from '@/lib/api/history/client';
import { ACTIVE_ORDERS_QK } from '@/lib/api/history/hooks';
import type { ActiveOrderPageResponse } from '@/lib/api/history/models';
import { useKycStatus } from '@/lib/api/kyc/hooks';
import { usePlaceLimitOrder, usePlaceMarketOrder } from '@/lib/api/order/hooks';
import type { OrderSide } from '@/lib/api/order/types';
// ❌ Removed: user-balances are updated via WebSocket, no need to fetch after order placement
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { DialogDeposit } from '@/lib/components/Account/Wallet/DialogDeposit';
import { ChampIcon } from '@/lib/components/Event/EventResultCard';
import { ExpirationDateSelect } from '@/lib/components/ExpirationDateSelect';
import { BuySellTabs } from '@/lib/components/OrderForm/BuySellTabs';
import { LimitConfirmDialog } from '@/lib/components/OrderForm/LimitConfirmDialog';
import { MarketConfirmDialog } from '@/lib/components/OrderForm/MarketConfirmDialog';
import { MarketIntentSwitch } from '@/lib/components/OrderForm/MarketIntentSwitch';
import { MarketOrderTypeSelect } from '@/lib/components/OrderForm/MarketOrderTypeSelect';
import { NumberInput } from '@/lib/components/OrderForm/NumberInput';
import { OrderFormHeader } from '@/lib/components/OrderForm/OrderFormHeader';
import { OrderFormTerms } from '@/lib/components/OrderForm/OrderFormTerms';
import { OrderFormWarning } from '@/lib/components/OrderForm/OrderFormWarning';
import { OrderSummary } from '@/lib/components/OrderForm/OrderSummary/OrderSummary';
import { QuickAddButtons } from '@/lib/components/OrderForm/QuickAddButtons';
import { FloatText } from '@/lib/components/Text/FloatText';
import { TranslatedText } from '@/lib/components/Text/TranslatedText';
import {
  useTradingSharesBalance,
  useTradingTotalSharesBalance,
} from '@/lib/components/Trading/TradingDataContext';
import { useAuthSession } from '@/lib/hooks/useAuth';
import { useIsBalanceHidden } from '@/lib/hooks/useIsBalanceHidden';
import { useMatchingSharesAtPrice } from '@/lib/hooks/useMatchingSharesAtPrice';
import { useOrderBookDataById } from '@/lib/hooks/useOrderBookServiceById';
import { useOrderPlacementWebSocket } from '@/lib/hooks/useOrderPlacementWebSocket';
import { useBalance } from '@/lib/providers/BalanceProvider';
import { useCoinsBalance } from '@/lib/providers/CoinsBalanceProvider';
import { useCoins } from '@/lib/providers/CoinsProvider';
import { useFees } from '@/lib/providers/FeesProvider';
import { useRouter } from '@/lib/routing';
import { useOrderFormStore, useSelectedOutcome } from '@/lib/store/order-form-store';
import { useOutcomePrices } from '@/lib/store/prices-store';
import { EMarketAction } from '@/lib/types/market-action';
import { EMarketIntent } from '@/lib/types/market-intent';
import { EMarketOrderType } from '@/lib/types/market-order-type';
import { Badge } from '@/lib/ui-kit/badge';
import { Text } from '@/lib/ui-kit/text';
import { Button } from '@/lib/ui-kit-v2/button';
import { Switch } from '@/lib/ui-kit-v2/switch';
import { calculateLimitFee, SHARE_DECIMALS } from '@/lib/utils/calculateFees';
import { capNumber } from '@/lib/utils/capNumber';
import { triggerSideCannonsConfetti } from '@/lib/utils/confetti';
import { formatMoney } from '@/lib/utils/formatAmount';
import { extractApiErrorCode } from '@/lib/utils/http';
import { floorTo } from '@/lib/utils/numbers';

const BILLION = 999_999_999;
const AMOUNT_ADD_OPTIONS = [20, 100, 1000];
const BUY_SHARES_ADD_OPTIONS = [-10, 10];
// 100% is covered by the dedicated +MAX chip — keep the row short to avoid wrap.
const SELL_SHARES_ADD_OPTIONS = [20, 50];

export function OrderForm() {
  const t = useTranslations('orderForm');
  const tCommon = useTranslations('common');
  const tToasts = useTranslations('toasts.orders');
  const tKyc = useTranslations('kyc');
  const { data: kycStatus } = useKycStatus();
  const isBalanceHidden = useIsBalanceHidden();

  const selectedOutcomeId = useOrderFormStore.use.selectedOutcomeId();
  const intent = useOrderFormStore.use.intent();
  const selectedAction = useOrderFormStore.use.action();
  const setIntent = useOrderFormStore.use.setIntent();
  const setAction = useOrderFormStore.use.setAction();
  const orderType = useOrderFormStore.use.orderType();
  const setOrderType = useOrderFormStore.use.setOrderType();
  const amountUsd = useOrderFormStore.use.amountUsd();
  const setAmountUsd = useOrderFormStore.use.setAmountUsd();
  const sharesCount = useOrderFormStore.use.sharesCount();
  const setSharesCount = useOrderFormStore.use.setSharesCount();
  const limitPrice = useOrderFormStore.use.limitPrice();
  const setLimitPrice = useOrderFormStore.use.setLimitPrice();
  const event = useOrderFormStore.use.event();
  const outcome = useSelectedOutcome();

  const { isAuthenticated } = useAuthSession();
  const isPaused = outcome?.status === 'PAUSED';
  const isAwaitingResolution = outcome?.status === 'AWAITING_RESOLUTION';
  const isResolved = outcome?.status === 'RESOLVED';
  const isTradingDisabled = isPaused || isAwaitingResolution || isResolved;

  const winningResolution = isResolved ? outcome?.r?.find((r) => r.isWin === true) : null;

  const tradingDisabledMessage = isPaused
    ? t('overlayPaused')
    : isAwaitingResolution
      ? t('overlayAwaitingResolution')
      : isResolved && winningResolution
        ? t('overlayWon', { result: winningResolution.isPositive ? tCommon('yes') : tCommon('no') })
        : isResolved
          ? t('overlayResolved')
          : '';

  const router = useRouter();
  const queryClient = useQueryClient();
  const { user } = useAuthContext();

  const placeMarketOrderMutation = usePlaceMarketOrder();
  const placeLimitOrderMutation = usePlaceLimitOrder();

  // WebSocket for order placement results
  useOrderPlacementWebSocket(
    (result) => {
      // Handle order placement result
      if (result.status === 'SUCCESS') {
        capture('order_placed', { order_id: result.orderId, event_id: event?.id });
        // Reset form on success
        setAmountUsd(new Decimal(0));
        setSharesCount(new Decimal(0));
        setLimitPrice(new Decimal(0));
      } else {
        capture('order_failed', {
          order_id: result.orderId,
          event_id: event?.id,
          error: result.error ?? undefined,
        });
      }
      // Error handling is done by the hook (toast notification)
    },
    {
      autoConnect: true,
      showToasts: true,
      invalidateQueries: false, // ❌ Disabled - invalidation is centralized in handleOrderAction
      outcomeId: selectedOutcomeId ?? undefined,
      eventId: event?.id ?? undefined,
      toastMessages: {
        error: tToasts('placementFailed'),
      },
    },
  );

  const livePrices = useOutcomePrices(selectedOutcomeId!);
  const orderBook = useOrderBookDataById(selectedOutcomeId ?? null);
  const { fees } = useFees();

  const matchingShares = useMatchingSharesAtPrice({
    outcomeId: selectedOutcomeId!,
    intent,
    action: selectedAction,
    price: limitPrice.div(100),
  });

  // Check if this is user's first order in the event
  // Returns true if user has no active orders in any outcome of the event
  const checkIfFirstOrderInEvent = async (
    eventData: NonNullable<typeof event>,
    client: ReturnType<typeof useQueryClient>,
    currentUserId: string | undefined,
  ): Promise<boolean> => {
    if (!eventData?.markets || eventData.markets.length === 0) {
      return false;
    }

    if (!currentUserId) {
      return false; // Can't check without user ID
    }

    try {
      // Check active orders for all outcomes in the event
      // Only count orders where current user is the owner (userId matches)
      const activeOrdersPromises = eventData.markets.map(async (outcome) => {
        const outcomeId = outcome.id;
        // Try to get from cache first
        const cachedData = client.getQueryData<ActiveOrderPageResponse>([
          ACTIVE_ORDERS_QK,
          outcomeId,
        ]);

        if (cachedData?.items && cachedData.items.length > 0) {
          // Check if any order belongs to current user
          const hasUserOrders = cachedData.items.some((order) => order.userId === currentUserId);

          return hasUserOrders;
        }

        // If not in cache, fetch it
        try {
          const activeOrders = await client.fetchQuery({
            queryKey: [ACTIVE_ORDERS_QK, outcomeId],
            queryFn: () => fetchActiveOrders({ eventOutcomeId: outcomeId, sort: ['asc'] }),
          });

          // Check if any order belongs to current user
          const hasUserOrders =
            activeOrders?.items &&
            activeOrders.items.some((order) => order.userId === currentUserId);

          return hasUserOrders ?? false;
        } catch (error) {
          // If fetch fails, assume no orders (conservative approach)
          console.error(
            `[Confetti Check] Error fetching active orders for outcome ${outcomeId}:`,
            error,
          );
          return false;
        }
      });

      const hasOrdersResults = await Promise.all(activeOrdersPromises);

      // If user has any orders in any outcome, this is not the first order
      const hasAnyOrders = hasOrdersResults.some((hasOrders) => hasOrders);

      return !hasAnyOrders; // Return true if no orders found (first order)
    } catch (error) {
      console.error('Error checking if first order in event:', error);
      // On error, don't show confetti (conservative approach)
      return false;
    }
  };

  // Synchronous guard against double-submit. `mutation.isPending` flips on the
  // next render, so on slow connections the user can spam-click Confirm before
  // React re-renders the disabled state, and each click queues another order.
  // The ref blocks re-entry the instant `executeOrderAction` starts running;
  // the state mirrors it so the dialog button shows the disabled/loading
  // appearance during the pre-flight `checkIfFirstOrderInEvent` await window
  // (before `mutation.isPending` becomes true).
  const isExecutingOrderRef = useRef(false);
  const [isExecutingOrder, setIsExecutingOrder] = useState(false);

  // Snapshot of the form for PostHog — the same shape for every order event.
  const describeOrder = (): OrderEventProps | null => {
    if (!event || !selectedOutcomeId) return null;
    const isMarket = orderType === EMarketOrderType.MARKET;
    const isBuy = selectedAction === EMarketAction.BUY;
    return {
      event_id: event.id,
      outcome_id: selectedOutcomeId,
      intent: intent === EMarketIntent.YES ? 'YES' : 'NO',
      side: isBuy ? 'BUY' : 'SELL',
      order_type: isMarket ? 'MARKET' : 'LIMIT',
      amount_usd: isMarket && isBuy ? amountUsd.toNumber() : undefined,
      shares: isMarket && isBuy ? undefined : sharesCount.toNumber(),
      limit_price: isMarket ? undefined : limitPrice.toNumber(),
    };
  };

  const executeOrderAction = async () => {
    if (isExecutingOrderRef.current) return;

    if (!event || !selectedOutcomeId || !outcome) {
      console.error('Missing required data: event, selectedOutcomeId, or outcome');
      return;
    }

    if (!assetId) {
      console.error('USDC assetId is not available');
      return;
    }

    if (!isAuthenticated) {
      console.error('Not authenticated');
      return;
    }

    isExecutingOrderRef.current = true;
    setIsExecutingOrder(true);

    // Check if this is user's first order in this event BEFORE creating the order
    const currentUserId: string | undefined = user && user.id ? user.id : undefined;
    const isFirstOrderInEvent = await checkIfFirstOrderInEvent(event, queryClient, currentUserId);

    // Find resolution by isPositive flag (YES/NO) from outcome.r array
    const isPositive = intent === EMarketIntent.YES;
    const resolution = outcome.r?.find((r) => r.isPositive === isPositive);

    if (!resolution) {
      console.error(`Resolution with isPositive=${isPositive} not found in outcome.r`);
      return;
    }

    const side: OrderSide = selectedAction === EMarketAction.BUY ? 'BUY' : 'SELL';
    const eventId = event.id;
    const eventOutcomeId = selectedOutcomeId;
    const baseAssetId = resolution.id; // Base asset is the resolution ID (YES or NO)
    const quoteAssetId = assetId; // Quote asset is USDC
    const orderProps = describeOrder();

    try {
      if (orderType === EMarketOrderType.MARKET) {
        // Market order: use amount for BUY, shares for SELL
        if (selectedAction === EMarketAction.BUY) {
          if (amountUsd.lte(0)) {
            console.error('Amount must be greater than 0');
            return;
          }
          await placeMarketOrderMutation.mutateAsync({
            eventId,
            eventOutcomeId,
            baseAssetId,
            quoteAssetId,
            side,
            amount: amountUsd.toNumber(),
          });
        } else {
          // SELL
          if (sharesCount.lte(0)) {
            console.error('Shares must be greater than 0');
            return;
          }
          await placeMarketOrderMutation.mutateAsync({
            eventId,
            eventOutcomeId,
            baseAssetId,
            quoteAssetId,
            side,
            shares: sharesCount.toNumber(),
          });
        }
      } else {
        // LIMIT order
        if (sharesCount.lte(0)) {
          console.error('Shares must be greater than 0');
          return;
        }
        if (limitPrice.lte(0) || limitPrice.gt(99)) {
          console.error('Limit price must be between 1 and 99');
          return;
        }

        // Prepare expireAt in UTC format if expiration is enabled
        const expireAt =
          expirationEnabled && expirationDate ? expirationDate.toISOString() : undefined;

        await placeLimitOrderMutation.mutateAsync({
          eventId,
          eventOutcomeId,
          baseAssetId,
          quoteAssetId,
          side,
          price: limitPrice.div(100).toNumber(), // Convert cents to decimal
          quantity: sharesCount.toNumber(),
          expireAt,
        });
      }

      // ✅ Data will be updated via WebSocket ORDER_UPDATE message from history-service
      // No need to manually fetch or invalidate queries

      if (orderProps) capture('order_submitted', orderProps);

      // Close dialogs immediately after successful order placement
      setIsMarketConfirmDialogOpen(false);
      setIsLimitConfirmDialogOpen(false);

      // Trigger confetti effect only for first order in event
      if (isFirstOrderInEvent) {
        triggerSideCannonsConfetti();
      }

      // TODO: Handle success (show toast, reset form, etc.)
    } catch (error) {
      if (orderProps) {
        capture('order_rejected', { ...orderProps, error_code: extractApiErrorCode(error) });
      }
      // Error handled by global MutationCache onError with translations
    } finally {
      isExecutingOrderRef.current = false;
      setIsExecutingOrder(false);
    }
  };

  const handleOrderAction = () => {
    if (!isAuthenticated) {
      capture('order_form_blocked', { event_id: event?.id, reason: 'auth_required' });
      // Save current URL to redirect back after login
      if (typeof window !== 'undefined') {
        localStorage.setItem('pendingForAuthUrl', window.location.pathname);
      }
      router.push('/auth/login');
      return;
    }

    // KYC close-only: a restricted user may close positions (SELL) but not open new ones (BUY).
    // The backend enforces this too (403); this keeps the UI honest.
    if (kycStatus?.restricted && selectedAction === EMarketAction.BUY) {
      capture('order_form_blocked', { event_id: event?.id, reason: 'kyc_restricted' });
      toast.error(tKyc('buyBlocked'));
      return;
    }

    const orderProps = describeOrder();
    if (orderProps) capture('order_form_submitted', orderProps);

    // Show confirmation dialog based on order type
    if (orderType === EMarketOrderType.MARKET) {
      setIsMarketConfirmDialogOpen(true);
    } else {
      // For LIMIT orders, show limit confirmation dialog
      setIsLimitConfirmDialogOpen(true);
    }
  };

  // Price calculation based on action and intent:
  // YES:
  // 1. Buy Yes = best ask (если null → 0.00¢)
  // 2. Sell Yes = best bid (если null → 0.00¢)
  // NO:
  // 3. Buy No = 1 - best bid (если best bid null → 0.00¢)
  // 4. Sell No = 1 - best ask (если best ask null → 0.00¢)
  const buyYes = useMemo(() => {
    if (selectedAction === EMarketAction.BUY) {
      // Buy Yes = best ask (если null → 0.00)
      if (typeof livePrices?.bestAsk === 'number') {
        return livePrices.bestAsk;
      }
      return 0;
    } else {
      // Sell Yes = best bid (если null → 0.00)
      if (typeof livePrices?.bestBid === 'number') {
        return livePrices.bestBid;
      }
      return 0;
    }
  }, [livePrices, selectedAction]);

  const buyNo = useMemo(() => {
    if (selectedAction === EMarketAction.BUY) {
      // Buy No = 1 - best bid (если best bid null → 0.00)
      if (typeof livePrices?.bestBid === 'number') {
        return 1 - livePrices.bestBid;
      }
      return 0;
    } else {
      // Sell No = 1 - best ask (если best ask null → 0.00)
      if (typeof livePrices?.bestAsk === 'number') {
        return 1 - livePrices.bestAsk;
      }
      return 0;
    }
  }, [livePrices, selectedAction]);

  const [expirationEnabled, setExpirationEnabled] = useState<boolean>(false);
  const [expirationDate, setExpirationDate] = useState<Date | undefined>(undefined);
  const [isDepositDialogOpen, setIsDepositDialogOpen] = useState<boolean>(false);

  // Reset expiration date to default when toggling expiration
  useEffect(() => {
    if (!expirationEnabled) {
      setExpirationDate(undefined);
    }
  }, [expirationEnabled]);
  const [isMarketConfirmDialogOpen, setIsMarketConfirmDialogOpen] = useState<boolean>(false);
  const [isLimitConfirmDialogOpen, setIsLimitConfirmDialogOpen] = useState<boolean>(false);

  const sharesBalance = useTradingSharesBalance(
    selectedOutcomeId ?? '',
    intent === EMarketIntent.YES ? 'yes' : 'no',
  );

  const _totalSharesBalance = useTradingTotalSharesBalance(
    selectedOutcomeId ?? '',
    intent === EMarketIntent.YES ? 'yes' : 'no',
  );

  // Balance logic - same as in Navbar
  const { coins } = useCoins();
  const { getAvailableBalance } = useCoinsBalance();
  const { balance: wsBalance } = useBalance();

  // Find USDC coin by shortName
  const usdcCoin = coins.find((coin) => coin.shortName.toUpperCase() === 'USDC');
  const usdcBalance = usdcCoin ? getAvailableBalance(usdcCoin.id) : null;

  // Get USDC assetId
  const assetId = useMemo(() => usdcCoin?.id || '', [usdcCoin]);

  // Use USDC available balance from the store (populated by REST initial data
  // and kept live by WebSocket); fall back to the legacy single-number balance.
  const currentBalance =
    usdcBalance !== null && usdcBalance !== undefined ? usdcBalance : wsBalance;

  // Check if insufficient funds for buy orders
  const isInsufficientFundsForBuy = useMemo(() => {
    if (!isAuthenticated) return false;
    if (isTradingDisabled) return false;
    if (selectedAction !== EMarketAction.BUY) return false;
    if (orderType === EMarketOrderType.MARKET) {
      return amountUsd.gt(currentBalance);
    } else {
      // For limit orders, calculate total cost including fees
      if (sharesCount.lte(0) || !fees) return false;
      const baseCost = sharesCount.times(limitPrice.div(100));
      // Same fee split as the summary and the confirm dialog — a limit that crosses
      // the book pays taker, so a maker-priced check lets through an order the
      // backend then rejects for insufficient funds.
      const openingFee = calculateLimitFee(
        limitPrice.div(100),
        sharesCount,
        matchingShares,
        EMarketAction.BUY,
        fees,
      );
      const totalCostWithFees = baseCost.plus(openingFee);
      return totalCostWithFees.gt(currentBalance);
    }
  }, [
    isAuthenticated,
    isTradingDisabled,
    selectedAction,
    orderType,
    amountUsd,
    sharesCount,
    limitPrice,
    currentBalance,
    fees,
    matchingShares,
  ]);

  // Check if insufficient shares for sell orders
  const isInsufficientSharesForSell = useMemo(() => {
    if (!isAuthenticated) return false;
    if (isTradingDisabled) return false;
    if (selectedAction !== EMarketAction.SELL) return false;
    if (sharesBalance === undefined) return false;
    return sharesCount.gt(sharesBalance);
  }, [isAuthenticated, isTradingDisabled, selectedAction, sharesCount, sharesBalance]);

  // Check if form fields are empty/invalid
  const isFormEmpty = useMemo(() => {
    if (orderType === EMarketOrderType.MARKET) {
      if (selectedAction === EMarketAction.BUY) {
        // Market Buy: amount must be > 0
        return amountUsd.lte(0);
      } else {
        // Market Sell: shares must be > 0
        return sharesCount.lte(0);
      }
    } else {
      // Limit orders: both price and shares must be > 0
      return limitPrice.lte(0) || sharesCount.lte(0);
    }
  }, [orderType, selectedAction, amountUsd, sharesCount, limitPrice]);

  // Check if there are offers/bids in the order book
  // For BUY orders we need asks (sellers), for SELL orders we need bids (buyers)
  const hasOffers = useMemo(() => {
    if (!orderBook) return false;
    if (selectedAction === EMarketAction.BUY) {
      const asks = orderBook[intent].asks;
      return asks && asks.length > 0;
    } else {
      const bids = orderBook[intent].bids;
      return bids && bids.length > 0;
    }
  }, [orderBook, intent, selectedAction]);

  const marketBuyForm: ReactNode = useMemo(() => {
    return (
      <div className="flex w-full flex-col items-end gap-2">
        <div className="flex w-full items-center justify-between text-[13px] leading-4 tracking-[-0.13px]">
          <span className="text-v2-text-primary">{t('amount')}</span>
          <div className="flex items-center gap-1">
            <span className="text-v2-text-primary opacity-50">{t('balance')}</span>
            {isBalanceHidden ? (
              <span className="text-v2-text-primary">******</span>
            ) : (
              <span className="inline-flex items-baseline whitespace-nowrap text-v2-text-primary tabular-nums">
                <span className="tabular-nums text-v2-text-primary">
                  {formatMoney(currentBalance, { prefix: '$' })}
                </span>
              </span>
            )}
          </div>
        </div>
        <NumberInput
          placeholder={'$0'}
          max={BILLION}
          min={0}
          prefix={'$'}
          fractional={3}
          value={amountUsd.toNumber()}
          onChange={setAmountUsd}
          className="h-11 w-full rounded-[10px] border border-v2-border"
        />
        <div className="flex w-full items-center justify-center gap-1">
          <QuickAddButtons
            addOptions={AMOUNT_ADD_OPTIONS}
            showMax
            showSign
            prefix={'$'}
            onMax={() => setAmountUsd(currentBalance)}
            onAdd={(val) =>
              setAmountUsd(capNumber({ value: amountUsd.plus(val), min: 0, max: BILLION }))
            }
          />
        </div>
      </div>
    );
  }, [amountUsd, setAmountUsd, currentBalance, isBalanceHidden, t]);

  const marketSellForm: ReactNode = useMemo(() => {
    return (
      <div className="flex w-full flex-col items-end gap-2">
        <div className="flex w-full items-center justify-between text-[13px] leading-4 tracking-[-0.13px]">
          <span className="text-v2-text-primary">{t('shares')}</span>
          <div className="flex items-center gap-1">
            <span className="text-v2-text-primary opacity-50">{t('available')}</span>
            {isBalanceHidden ? (
              <span className="text-v2-text-primary">******</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-v2-text-primary">
                <FloatText value={sharesBalance ?? 0} /> {t('shares')}
              </span>
            )}
          </div>
        </div>
        <NumberInput
          placeholder={'0'}
          max={BILLION}
          fractional={2}
          value={sharesCount.toNumber()}
          onChange={setSharesCount}
          animated={false}
          className="h-11 w-full rounded-[10px] border border-v2-border"
        />
        <div className="flex w-full items-center justify-center gap-1">
          <QuickAddButtons
            addOptions={SELL_SHARES_ADD_OPTIONS}
            suffix={'%'}
            showMax
            onMax={() =>
              setSharesCount(capNumber({ value: sharesBalance ?? 0, min: 0, max: BILLION }))
            }
            onAdd={(percentage) => {
              if (sharesBalance !== undefined) {
                const percentageDecimal = new Decimal(percentage).div(100);
                // Quantities live on the lot grid — a raw 20% of 9.47 (1.894) would
                // display as 1.89 while 1.894 goes to the backend.
                const calculatedShares = floorTo(
                  new Decimal(sharesBalance).times(percentageDecimal),
                  SHARE_DECIMALS,
                );
                setSharesCount(capNumber({ value: calculatedShares, min: 0, max: BILLION }));
              }
            }}
          />
        </div>
      </div>
    );
  }, [setSharesCount, sharesBalance, sharesCount, isBalanceHidden, t]);

  const limitBuyForm: ReactNode = useMemo(() => {
    return (
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full flex-col items-end gap-2">
          <div className="flex w-full items-center justify-between text-[13px] leading-4 tracking-[-0.13px]">
            <span className="text-v2-text-primary">{t('limitPrice')}</span>
            <div className="flex items-center gap-1">
              <span className="text-v2-text-primary opacity-50">{t('balance')}</span>
              {isBalanceHidden ? (
                <span className="text-v2-text-primary">******</span>
              ) : (
                <span className="inline-flex items-baseline whitespace-nowrap text-v2-text-primary tabular-nums">
                  <span className="tabular-nums text-v2-text-primary">
                    {formatMoney(currentBalance, { prefix: '$' })}
                  </span>
                </span>
              )}
            </div>
          </div>
          <NumberInput
            placeholder={'0¢'}
            suffix={'¢'}
            showButtons={true}
            min={1}
            max={99}
            fractional={0}
            step={1}
            value={limitPrice.toNumber()}
            onChange={setLimitPrice}
            className="h-11 w-full rounded-[10px] border border-v2-border"
          />
        </div>
        <div className="flex w-full flex-col items-end gap-2">
          <div className="flex w-full items-center justify-between text-[13px] leading-4 tracking-[-0.13px]">
            <span className="text-v2-text-primary">{t('shares')}</span>
          </div>
          <NumberInput
            placeholder={'0'}
            max={BILLION}
            min={0}
            fractional={2}
            value={sharesCount.toNumber()}
            onChange={setSharesCount}
            animated={false}
            className="h-11 w-full rounded-[10px] border border-v2-border"
          />
          <div className="flex w-full items-center justify-between gap-2">
            {sharesCount.gt(0) ? (
              <Badge variant="success" className="rounded-[4px] px-1 py-0.5">
                <FloatText
                  variant="label-md"
                  value={Decimal.min(matchingShares, sharesCount)}
                  decimals={2}
                />{' '}
                <Text variant="label-md">{t('matching')}</Text>
              </Badge>
            ) : (
              <div />
            )}
            <QuickAddButtons
              addOptions={BUY_SHARES_ADD_OPTIONS}
              showSign
              onAdd={(val) =>
                setSharesCount(capNumber({ value: sharesCount.add(val), min: 0, max: BILLION }))
              }
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full items-center justify-between">
            <span className="text-[13px] leading-4 tracking-[-0.13px] text-v2-text-secondary">
              {t('setExpiration')}
            </span>
            <Switch checked={expirationEnabled} onCheckedChange={setExpirationEnabled} />
          </div>
          {expirationEnabled && (
            <ExpirationDateSelect value={expirationDate} onChange={setExpirationDate} />
          )}
        </div>
      </div>
    );
  }, [
    expirationDate,
    expirationEnabled,
    limitPrice,
    matchingShares,
    setLimitPrice,
    setSharesCount,
    sharesCount,
    currentBalance,
    isBalanceHidden,
    t,
  ]);

  const limitSellForm: ReactNode = useMemo(() => {
    return (
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full flex-col items-end gap-2">
          <div className="flex w-full items-center justify-between text-[13px] leading-4 tracking-[-0.13px]">
            <span className="text-v2-text-primary">{t('limitPrice')}</span>
            <div className="flex items-center gap-1">
              <span className="text-v2-text-primary opacity-50">{t('balance')}</span>
              {isBalanceHidden ? (
                <span className="text-v2-text-primary">******</span>
              ) : (
                <span className="inline-flex items-baseline whitespace-nowrap text-v2-text-primary tabular-nums">
                  <span className="tabular-nums text-v2-text-primary">
                    {formatMoney(currentBalance, { prefix: '$' })}
                  </span>
                </span>
              )}
            </div>
          </div>
          <NumberInput
            placeholder={'0¢'}
            suffix={'¢'}
            showButtons={true}
            min={1}
            max={99}
            fractional={0}
            step={1}
            value={limitPrice.toNumber()}
            onChange={setLimitPrice}
            className="h-11 w-full rounded-[10px] border border-v2-border"
          />
        </div>
        <div className="flex w-full flex-col items-end gap-2">
          <div className="flex w-full items-center justify-between text-[13px] leading-4 tracking-[-0.13px]">
            <span className="text-v2-text-primary">{t('shares')}</span>
            <div className="flex items-center gap-1">
              <span className="text-v2-text-primary opacity-50">{t('available')}</span>
              {isBalanceHidden ? (
                <span className="text-v2-text-primary">******</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-v2-text-primary">
                  <FloatText value={sharesBalance ?? 0} /> {t('shares')}
                </span>
              )}
            </div>
          </div>
          <NumberInput
            placeholder={'0'}
            max={BILLION}
            fractional={2}
            value={sharesCount.toNumber()}
            onChange={setSharesCount}
            animated={false}
            className="h-11 w-full rounded-[10px] border border-v2-border"
          />
          <div className="flex w-full items-center justify-between gap-2">
            {sharesCount.gt(0) &&
            !(selectedAction === EMarketAction.SELL && intent === EMarketIntent.NO) ? (
              <Badge variant="success" className="rounded-[4px] px-1 py-0.5">
                <FloatText
                  variant="label-md"
                  value={Decimal.min(matchingShares, sharesCount)}
                  decimals={2}
                />{' '}
                <Text variant="label-md">{t('matching')}</Text>
              </Badge>
            ) : (
              <div />
            )}
            <QuickAddButtons
              addOptions={SELL_SHARES_ADD_OPTIONS}
              suffix={'%'}
              showMax
              onMax={() =>
                setSharesCount(capNumber({ value: sharesBalance ?? 0, min: 0, max: BILLION }))
              }
              onAdd={(percentage) => {
                if (sharesBalance !== undefined) {
                  const percentageDecimal = new Decimal(percentage).div(100);
                  // Quantities live on the lot grid — a raw 20% of 9.47 (1.894) would
                  // display as 1.89 while 1.894 goes to the backend.
                  const calculatedShares = floorTo(
                    new Decimal(sharesBalance).times(percentageDecimal),
                    SHARE_DECIMALS,
                  );
                  setSharesCount(capNumber({ value: calculatedShares, min: 0, max: BILLION }));
                }
              }}
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full items-center justify-between">
            <span className="text-[13px] leading-4 tracking-[-0.13px] text-v2-text-secondary">
              {t('setExpiration')}
            </span>
            <Switch checked={expirationEnabled} onCheckedChange={setExpirationEnabled} />
          </div>
          {expirationEnabled && (
            <ExpirationDateSelect value={expirationDate} onChange={setExpirationDate} />
          )}
        </div>
      </div>
    );
  }, [
    expirationDate,
    expirationEnabled,
    limitPrice,
    matchingShares,
    setLimitPrice,
    setSharesCount,
    sharesCount,
    sharesBalance,
    isBalanceHidden,
    intent,
    selectedAction,
    currentBalance,
    t,
  ]);

  return (
    <div className="flex max-h-full flex-1 flex-col gap-6 overflow-hidden">
      {isTradingDisabled ? (
        <div className="flex min-h-[313px] w-full flex-1 flex-col items-center justify-center gap-3">
          {isPaused ? (
            <svg
              width="56"
              height="56"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 10.0007C10 9.55862 10.1756 9.1347 10.4882 8.82214C10.8007 8.50958 11.2246 8.33398 11.6667 8.33398H15C15.442 8.33398 15.866 8.50958 16.1785 8.82214C16.4911 9.1347 16.6667 9.55862 16.6667 10.0007V30.0007C16.6667 30.4427 16.4911 30.8666 16.1785 31.1792C15.866 31.4917 15.442 31.6673 15 31.6673H11.6667C11.2246 31.6673 10.8007 31.4917 10.4882 31.1792C10.1756 30.8666 10 30.4427 10 30.0007V10.0007Z"
                fill="#034DBA"
                stroke="#4D8DF9"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M23.3333 10.0007C23.3333 9.55862 23.5089 9.1347 23.8215 8.82214C24.1341 8.50958 24.558 8.33398 25 8.33398H28.3333C28.7754 8.33398 29.1993 8.50958 29.5119 8.82214C29.8244 9.1347 30 9.55862 30 10.0007V30.0007C30 30.4427 29.8244 30.8666 29.5119 31.1792C29.1993 31.4917 28.7754 31.6673 28.3333 31.6673H25C24.558 31.6673 24.1341 31.4917 23.8215 31.1792C23.5089 30.8666 23.3333 30.4427 23.3333 30.0007V10.0007Z"
                fill="#034DBA"
                stroke="#4D8DF9"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : isAwaitingResolution ? (
            <svg
              width="56"
              height="56"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.58337 6.66602H27.25C31.75 6.66602 32.75 9.16602 29.5834 12.3327L27.5834 14.3327C26.25 15.666 26.25 17.8327 27.5834 18.9993L29.5834 20.9993C32.75 24.166 31.5834 26.666 27.25 26.666H8.58337"
                fill="#8BB6FF"
              />
              <path
                d="M8.58337 6.66602H27.25C31.75 6.66602 32.75 9.16602 29.5834 12.3327L27.5834 14.3327C26.25 15.666 26.25 17.8327 27.5834 18.9993L29.5834 20.9993C32.75 24.166 31.5834 26.666 27.25 26.666H8.58337"
                stroke="#4D8DF9"
                strokeWidth="3"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.58337 3.33398V36.6673"
                stroke="#4D8DF9"
                strokeWidth="3"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4.69345 35.6381L3.52874 38.6381C3.27427 39.2935 3.75783 40 4.46095 40H12.539C13.2422 40 13.7257 39.2935 13.4713 38.6381L12.3066 35.6381C12.1572 35.2534 11.787 35 11.3743 35H5.62566C5.21304 35 4.84279 35.2534 4.69345 35.6381Z"
                fill="#1B70EE"
              />
            </svg>
          ) : isResolved && winningResolution ? (
            <ChampIcon className="size-14" />
          ) : (
            <svg
              width="56"
              height="56"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="20" cy="20" r="18" fill="#E53935" />
              <path d="M14 14L26 26" stroke="white" strokeWidth="3" strokeLinecap="round" />
              <path d="M26 14L14 26" stroke="white" strokeWidth="3" strokeLinecap="round" />
            </svg>
          )}
          <Text variant="title-xs" className="text-center font-semibold text-v2-text-secondary">
            {tradingDisabledMessage}
          </Text>
        </div>
      ) : (
        <>
          <div className="flex w-full flex-col gap-4">
            <OrderFormHeader />
            <div className="relative flex w-full items-center justify-between">
              <BuySellTabs activeTab={selectedAction} onActiveTabChange={setAction} />
              <MarketOrderTypeSelect value={orderType} onValueChange={setOrderType} />
            </div>
          </div>

          <MarketIntentSwitch
            yesPrice={buyYes}
            noPrice={buyNo}
            value={intent}
            onChange={setIntent}
          />

          <div className="flex flex-1 flex-col gap-4 overflow-auto">
            {orderType === EMarketOrderType.MARKET && selectedAction === EMarketAction.BUY
              ? marketBuyForm
              : null}

            {orderType === EMarketOrderType.MARKET && selectedAction === EMarketAction.SELL
              ? marketSellForm
              : null}

            {orderType === EMarketOrderType.LIMIT && selectedAction === EMarketAction.BUY
              ? limitBuyForm
              : null}
            {orderType === EMarketOrderType.LIMIT && selectedAction === EMarketAction.SELL
              ? limitSellForm
              : null}
          </div>

          {isInsufficientFundsForBuy && (
            <OrderFormWarning onDepositClick={() => setIsDepositDialogOpen(true)} />
          )}
          {isInsufficientSharesForSell && <OrderFormWarning showDeposit={false} />}

          <div className="flex flex-col gap-4">
            <Button
              appearance="accent"
              size="M"
              className="h-10 w-full"
              onClick={handleOrderAction}
              disabled={
                isAuthenticated &&
                (placeMarketOrderMutation.isPending ||
                  placeLimitOrderMutation.isPending ||
                  isFormEmpty ||
                  isInsufficientFundsForBuy ||
                  isInsufficientSharesForSell ||
                  (orderType === EMarketOrderType.MARKET && !hasOffers))
              }
            >
              {placeMarketOrderMutation.isPending || placeLimitOrderMutation.isPending ? (
                <Text>{t('processing')}</Text>
              ) : isAuthenticated ? (
                <TranslatedText
                  token={selectedAction === EMarketAction.BUY ? 'common.buy' : 'common.sell'}
                />
              ) : (
                <Text>{t('getStarted')}</Text>
              )}
            </Button>
            <OrderSummary />
            <OrderFormTerms />
          </div>
          <DialogDeposit isOpen={isDepositDialogOpen} setIsOpen={setIsDepositDialogOpen} />
          <MarketConfirmDialog
            isOpen={isMarketConfirmDialogOpen}
            onClose={() => setIsMarketConfirmDialogOpen(false)}
            onConfirm={executeOrderAction}
            isProcessing={isExecutingOrder || placeMarketOrderMutation.isPending}
          />
          <LimitConfirmDialog
            isOpen={isLimitConfirmDialogOpen}
            onClose={() => setIsLimitConfirmDialogOpen(false)}
            onConfirm={executeOrderAction}
            isProcessing={isExecutingOrder || placeLimitOrderMutation.isPending}
          />
        </>
      )}
    </div>
  );
}
